import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  ConfirmDialog,
  InfoBox,
  Input,
  LoadingState,
  Toast,
} from '@biznomi/ui-react'
import { manualReasonLabel, ABSENCE_REASONS } from '@biznomi/types'
import { getOrgSettings, updateOrgSettings, type OrgSettings } from '@/lib/api/admin'
import { LEAVE_TYPES, type LeaveConfig, type LeaveType } from '@/lib/api/leave'
import type { OrgUiConfig } from '@/lib/api/auth'
import { meetsPlan } from '@/lib/plan'
import { useOrg } from '@/lib/useOrg'

/**
 * 조직 설정 — 최종관리자 전용 (서버도 403).
 *
 * ⚠️ 이 파일은 judgmentMode 를 다루도록 허용된 곳이다 (가드레일 #2 ALLOWLIST).
 *    여기서 하는 일은 값을 **고르고 저장하는 것**뿐이다. 지각·조퇴를 판단하지 않는다.
 *    판단은 AttendanceTime 안에서만 한다.
 *
 * 담는 것은 화면 취향과 근무 기준까지다. 급여·연차 발생·법 해석은 넣지 않는다.
 */

function Section({ title, desc, children }: { title: string; desc?: string; children: React.ReactNode }) {
  return (
    <section className="rounded-card bg-card p-4">
      <h2 className="text-sm font-bold">{title}</h2>
      {desc && <p className="mt-0.5 text-xs text-slate-400">{desc}</p>}
      <div className="mt-3 space-y-3">{children}</div>
    </section>
  )
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string
  hint?: string
  checked: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-[var(--color-primary)]"
      />
      <span className="min-w-0">
        <span className="block text-sm">{label}</span>
        {hint && <span className="mt-0.5 block text-xs text-slate-400">{hint}</span>}
      </span>
    </label>
  )
}

export default function AdminSettings() {
  const qc = useQueryClient()
  // 휴가 종류 칸은 PRO + features.leave 일 때만 나온다. 훅이라 이른 return 앞에서 부른다
  const { data: org } = useOrg()
  const [form, setForm] = useState<OrgSettings | null>(null)
  const [toast, setToast] = useState('')
  const [toastError, setToastError] = useState(false)
  // 판정 모드는 되묻는다 — 켜는 순간 전 직원 화면에 지각·조퇴가 붙는다
  const [confirmJudgment, setConfirmJudgment] = useState<'STANDARD' | 'FACTUAL' | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'org-settings'],
    queryFn: async () => {
      const res = await getOrgSettings()
      if (!res.success || !res.data) throw new Error(res.error ?? '설정을 불러오지 못했습니다')
      return res.data
    },
  })

  useEffect(() => {
    if (data) setForm({ ...data, uiConfig: data.uiConfig ?? {} })
  }, [data])

  const save = useMutation({
    mutationFn: (body: Partial<OrgSettings>) => updateOrgSettings(body),
    onSuccess: (res) => {
      if (!res.success) {
        setToastError(true)
        setToast(res.error ?? '저장하지 못했습니다')
        return
      }
      setToastError(false)
      setToast('저장했습니다')
      qc.invalidateQueries({ queryKey: ['admin', 'org-settings'] })
      // 화면 설정·판정 모드는 조직 정보에서도 읽으므로 같이 새로 받는다
      qc.invalidateQueries({ queryKey: ['org'] })
      // 휴가 신청 화면이 GET /api/leaves/config 를 따로 캐시한다. 안 버리면
      // 규정을 바꾼 직후에도 옛 값으로 「차감하지 않습니다」를 말한다
      qc.invalidateQueries({ queryKey: ['leave'] })
    },
  })

  if (isLoading || !form) return <LoadingState />
  if (error) return <InfoBox tone="danger">{(error as Error).message}</InfoBox>

  const ui: OrgUiConfig = form.uiConfig ?? {}
  const setUi = (patch: Partial<OrgUiConfig>) =>
    setForm({ ...form, uiConfig: { ...ui, ...patch } })

  const standard = form.judgmentMode === 'STANDARD'

  /*
   * 휴가 종류별 규정. 서버가 기본값을 얹어 보내므로 여기 값은 이미 채워져 있다.
   * 🔴 무엇을 차감할지 우리가 정하지 않는다 — 회사가 고른 값을 나르기만 한다.
   */
  const cfg: LeaveConfig = form.leaveConfig ?? { deduct: {}, label: {}, enabled: {} }
  const setLeaveFlag = (key: 'deduct' | 'enabled', t: LeaveType, v: boolean) =>
    setForm({ ...form, leaveConfig: { ...cfg, [key]: { ...cfg[key], [t]: v } } })
  const setLeaveLabel = (t: LeaveType, v: string) =>
    setForm({ ...form, leaveConfig: { ...cfg, label: { ...cfg.label, [t]: v } } })
  // 휴가는 축이 둘이다 (플랜 PRO + features.leave). 하나만 보면 BASIC 에서도 칸이 뜬다
  const leaveOn = form.features?.leave === true && meetsPlan(org?.plan, 'PRO')

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-xl font-bold">조직 설정</h1>
        <p className="mt-1 text-sm text-slate-500">이 회사에서만 적용됩니다</p>
      </header>

      <Section title="근무 시간" desc="출퇴근 기준 시각입니다. 이 시각으로 무언가를 자동 처리하지는 않습니다.">
        <div className="flex items-center gap-2">
          <label htmlFor="ws" className="sr-only">출근 기준</label>
          <Input
            id="ws"
            type="time"
            value={form.workStartTime}
            onChange={(e) => setForm({ ...form, workStartTime: e.target.value })}
            className="h-10"
          />
          <span className="shrink-0 text-slate-400">~</span>
          <label htmlFor="we" className="sr-only">퇴근 기준</label>
          <Input
            id="we"
            type="time"
            value={form.workEndTime}
            onChange={(e) => setForm({ ...form, workEndTime: e.target.value })}
            className="h-10"
          />
        </div>
      </Section>

      {/*
       * ★ 판정 모드.
       *   기본은 '사실만' 이다. 켜는 것은 회사가 명시적으로 정하는 일이고,
       *   바꾸면 감사 로그에 남는다 — 나중에 "언제부터 지각으로 찍혔냐" 에 답해야 한다.
       */}
      <Section
        title="지각·조퇴 표시"
        desc="화면에만 표시됩니다. 기록에는 저장되지 않고, 껐다 켜도 지난 기록이 바뀌지 않습니다."
      >
        <div className="grid gap-2 sm:grid-cols-2">
          {([
            ['FACTUAL', '사실만', '찍힌 시각을 그대로 보여줍니다'],
            ['STANDARD', '표준', '기준 시각보다 늦으면 “지각”을 함께 보여줍니다'],
          ] as const).map(([mode, label, hint]) => (
            <button
              key={mode}
              type="button"
              onClick={() => form.judgmentMode !== mode && setConfirmJudgment(mode)}
              className={
                'rounded-lg border px-3 py-2.5 text-left ' +
                (form.judgmentMode === mode
                  ? 'border-primary bg-primary/10'
                  : 'border-slate-200 bg-white')
              }
            >
              <span className="block text-sm font-semibold">{label}</span>
              <span className="mt-0.5 block text-xs text-slate-400">{hint}</span>
            </button>
          ))}
        </div>

        {standard && (
          <div className="space-y-3 border-t border-slate-100 pt-3">
            <div>
              <label htmlFor="grace" className="mb-1 block text-sm text-slate-500">
                지각 유예 (분)
              </label>
              <Input
                id="grace"
                type="number"
                min={0}
                value={form.lateGraceMinutes}
                onChange={(e) => setForm({ ...form, lateGraceMinutes: Number(e.target.value) })}
                className="h-10 w-32"
              />
              <p className="mt-1 text-xs text-slate-400">
                {form.workStartTime} 출근인데 {form.lateGraceMinutes}분까지는 지각으로 보지 않습니다.
              </p>
            </div>
            <div>
              <label htmlFor="early" className="mb-1 block text-sm text-slate-500">
                조퇴 기준 (분)
              </label>
              <Input
                id="early"
                type="number"
                min={0}
                value={form.earlyLeaveMinutes}
                onChange={(e) => setForm({ ...form, earlyLeaveMinutes: Number(e.target.value) })}
                className="h-10 w-32"
              />
              <p className="mt-1 text-xs text-slate-400">
                퇴근 {form.workEndTime} 기준, {form.earlyLeaveMinutes}분 전보다 일찍 나가면 조퇴로 봅니다.
              </p>
            </div>
          </div>
        )}

        <InfoBox tone="info">
          이 설정을 바꾸면 감사 로그에 남습니다. 나중에 “언제부터 이렇게 보였나”를 확인할 수 있어야
          하기 때문입니다.
        </InfoBox>
      </Section>

      <Section title="직원 화면" desc="직원 앱에 무엇을 보여줄지">
        <Toggle
          label="기준 시각 보여주기"
          hint="지각·조퇴 옆의 “기준 09:00” 표시. 끄면 배지만 남습니다"
          checked={ui.showWorkHours !== false}
          onChange={(v) => setUi({ showWorkHours: v })}
        />
        <Toggle
          label="이번 달 출근일수 보여주기"
          hint="직원이 자기 출근일수를 홈에서 봅니다. 사장이 받는 엑셀과 같은 숫자입니다"
          checked={ui.showMonthlySummary !== false}
          onChange={(v) => setUi({ showMonthlySummary: v })}
        />
      </Section>

      <Section title="안내 문구" desc="직원 화면 위쪽에 한 줄로 뜹니다">
        <Toggle
          label="안내 문구 사용"
          checked={ui.noticeBanner?.enabled === true}
          onChange={(v) => setUi({ noticeBanner: { text: ui.noticeBanner?.text ?? '', enabled: v } })}
        />
        {ui.noticeBanner?.enabled && (
          <Input
            value={ui.noticeBanner?.text ?? ''}
            onChange={(e) => setUi({ noticeBanner: { text: e.target.value, enabled: true } })}
            maxLength={100}
            placeholder="예: 8월 15일은 휴무입니다"
            className="h-10"
          />
        )}
      </Section>

      {/* enum 을 하드코딩하지 않고 순회한다 — 사유가 늘면 여기도 자동으로 늘어난다 */}
      <Section title="부재 사유" desc="직원이 고를 수 있는 항목. 아무것도 안 고르면 전부 보입니다.">
        <div className="flex flex-wrap gap-2">
          {ABSENCE_REASONS.map((r) => {
            const list = ui.absenceReasons ?? []
            const on = list.length === 0 || list.includes(r)
            return (
              <button
                key={r}
                type="button"
                onClick={() => {
                  const base = list.length === 0 ? [...ABSENCE_REASONS] : list
                  const next = base.includes(r) ? base.filter((x) => x !== r) : [...base, r]
                  setUi({ absenceReasons: next })
                }}
                className={
                  'rounded-full border px-3 py-1.5 text-sm ' +
                  (on ? 'border-primary bg-primary/10 font-semibold text-primary' : 'border-slate-200 bg-white text-slate-500')
                }
              >
                {manualReasonLabel(r)}
              </button>
            )
          })}
        </div>
      </Section>

      {/*
       * '기록 수정 권한' 도 여기 없다.
       *   서버는 지금 최종관리자만 남의 기록을 고칠 수 있게 못 박혀 있고
       *   (attendance.controller — 부서장의 타 직원 기록 수정 권한은 의도적으로 뺐다),
       *   이 화면에 스위치를 두면 "관리자도 가능" 을 골라도 서버가 403 을 낸다.
       *   권한을 실제로 넓히는 건 대리 수정 경로를 여는 결정이라 설정 화면이 아니라
       *   따로 판단할 일이다.
       */}
      {/*
        * 🔴 이 칸이 없어서 신청 화면이 「회사가 정한 규정입니다」라고 말해 놓고
        *   회사가 못 정했다. psql 을 쳐야 했다 (2026-09-11).
        */}
      {leaveOn && (
        <Section title="휴가 종류" desc="이 회사에서 쓰는 종류와 그 규정입니다.">
          <div className="space-y-3">
            {LEAVE_TYPES.map((t) => (
              // data-type 은 캡처가 종류별 토글을 집는 손잡이다 — 토글 이름이 종류마다
              // 같아서(«연차에서 차감하기» 가 여섯 번) 이름만으로는 누구 것인지 모른다
              <div key={t.value} data-type={t.value} className="rounded-lg border border-slate-200 p-3">
                <label htmlFor={`lt-${t.value}`} className="mb-1 block text-xs text-slate-400">
                  {t.label}
                </label>
                <Input
                  id={`lt-${t.value}`}
                  value={cfg.label[t.value] ?? ''}
                  onChange={(e) => setLeaveLabel(t.value, e.target.value)}
                  placeholder={t.label}
                  maxLength={20}
                  className="h-9"
                />
                <div className="mt-2.5 space-y-2">
                  <Toggle
                    label="직원이 고르게 하기"
                    hint="끄면 신청 화면에서 이 종류가 사라집니다"
                    checked={cfg.enabled[t.value] !== false}
                    onChange={(v) => setLeaveFlag('enabled', t.value, v)}
                  />
                  <Toggle
                    label="연차에서 차감하기"
                    hint="끄면 승인해도 남은 일수가 줄지 않습니다"
                    checked={cfg.deduct[t.value] !== false}
                    onChange={(v) => setLeaveFlag('deduct', t.value, v)}
                  />
                </div>
              </div>
            ))}
          </div>
          <InfoBox tone="info">
            무엇을 차감할지는 회사가 정합니다. 회사 규정에 맞게 켜고 끄세요.
            이름을 비우면 기본 이름으로 돌아갑니다.
          </InfoBox>
        </Section>
      )}

      <InfoBox tone="info">
        출퇴근 기록 수정은 최종관리자만 할 수 있습니다. 관리자(부서장)는 승인·반려만 합니다.
      </InfoBox>

      <Button
        size="lg"
        className="w-full"
        loading={save.isPending}
        onClick={() =>
          save.mutate({
            workStartTime: form.workStartTime,
            workEndTime: form.workEndTime,
            lateGraceMinutes: form.lateGraceMinutes,
            earlyLeaveMinutes: form.earlyLeaveMinutes,
            uiConfig: form.uiConfig,
            // BASIC 조직은 칸 자체가 안 뜬다. 그때는 보내지 않는다 — 안 보이는 칸이
            // 값을 덮어쓰면 「안 만진 설정이 바뀌는」 모양이 된다
            ...(leaveOn ? { leaveConfig: form.leaveConfig } : {}),
            judgmentMode: form.judgmentMode,
          })
        }
      >
        저장
      </Button>

      <ConfirmDialog
        open={confirmJudgment !== null}
        onOpenChange={(o) => !o && setConfirmJudgment(null)}
        title={confirmJudgment === 'STANDARD' ? '지각·조퇴를 표시할까요?' : '사실만 표시할까요?'}
        message={
          confirmJudgment === 'STANDARD'
            ? '기준 시각보다 늦게 찍힌 기록에 “지각”이 함께 보입니다.\n기록 자체는 바뀌지 않고, 화면 표시만 달라집니다.\n\n이 변경은 감사 로그에 남습니다.'
            : '지각·조퇴 표시가 사라지고 찍힌 시각만 보입니다.\n지난 기록은 그대로입니다.\n\n이 변경은 감사 로그에 남습니다.'
        }
        confirmText="바꾸기"
        onConfirm={() => {
          if (confirmJudgment) setForm({ ...form, judgmentMode: confirmJudgment })
          setConfirmJudgment(null)
        }}
      />

      {toast && (
        <Toast message={toast} variant={toastError ? 'error' : 'success'} onClose={() => setToast('')} />
      )}
    </div>
  )
}
